/* eslint-disable import/no-duplicates */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  Button,
  Icon,
  Center,
  Tooltip,
  HStack,
  VStack,
  Switch,
  FormControl,
  FormLabel,
  Select,
  Input,
  Collapse,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import {
  RiDeleteBinLine,
  RiDraftLine,
  RiCheckboxCircleLine,
  RiShieldCheckLine,
  RiAttachmentLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiArrowUpDownLine,
  RiSendPlaneLine,
  RiFilterLine,
  RiCloseLine,
  RiGitMergeLine,
} from 'react-icons/ri';
import { formatReportDate } from '../../../../utils/formatReportDate';

import { ModalAddNewExpense } from '../../Modal/ModalAddNewExpense';
import { ModalEditExpense } from '../../Modal/ModalEditExpense';
import { ModalViewExpense } from '../../Modal/ModalViewExpense';
import { ModalConsolidatedChildren } from '../../Modal/ModalConsolidatedChildren';
import { ModalConsolidateExpenses } from '../../Modal/ModalConsolidateExpenses';
import { ModalSendForSignature } from '../../Modal/ModalSendForSignature';
import { ModalUpdateExpenseStatus } from '../../Modal/ModalUpdateExpenseStatus';
import { ModalRevertRessarcimento } from '../../Modal/ModalRevertRessarcimento';
import { Pagination } from '../../Pagination';
import { IExpenseReport } from '../../../../pages/Dashboard/vehicles_management/Expenses';
import { useUsers } from '../../../../hooks/useUsers';
import usePersistedState from '../../../../hooks/usePersistedState';
import { expenseReportsService } from '../../../../services/expenseReportsService';
import { apllyToast } from '../../../Global/Toast2.0';


// Wrapper para Badge para evitar problemas de tipos complexos
const ColorBadge = ({
  color,
  fontSize,
  children,
}: {
  color: string;
  fontSize?: string;
  children: React.ReactNode;
}) => {
  return React.createElement(
    Badge,
    { colorScheme: color as any, fontSize: fontSize as any },
    children,
  );
};

interface IExpensesTableProps {
  expenseReports: IExpenseReport[];
  onAdd: (
    report: Omit<IExpenseReport, 'id' | 'created_at' | 'updated_at'>,
  ) => void;
  onEdit: (id: string, report: Partial<IExpenseReport>) => void;
  onDelete: (id: string) => void;
  onConsolidate: (
    report: Omit<IExpenseReport, 'id' | 'created_at' | 'updated_at'>,
    originalReportIds: string[],
    period: { startDate: string; endDate: string },
  ) => void;
  onRefresh?: () => void;
  isAdmin?: boolean;
}

type SortColumn = 'date' | 'user' | 'items' | 'total' | 'signature' | 'status' | 'due_date';
type SortDirection = 'asc' | 'desc';

export function ExpensesTable({
  expenseReports,
  onAdd,
  onEdit,
  onDelete,
  onConsolidate,
  onRefresh,
  isAdmin = false,
}: IExpensesTableProps) {
  const [page, setPage] = useState(1);
  const [showConsolidated, setShowConsolidated] = usePersistedState(
    '@samasc:expenses:showConsolidated',
    false,
  );
  const [sortColumn, setSortColumn] = useState<SortColumn>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [sendingAccountingId, setSendingAccountingId] = useState('');
  const [groupBy, setGroupBy] = usePersistedState(
    '@samasc:expenses:groupBy',
    'sector',
  );
  const { users, getUserName } = useUsers();
  const { isOpen: filtersOpen, onToggle: toggleFilters } = useDisclosure();
  const itemsPerPage = 6;

  // Estados dos filtros (persistidos no localStorage para manter a seleção
  // entre recarregamentos/navegação).
  const [filterUserId, setFilterUserId] = usePersistedState(
    '@samasc:expenses:filterUserId',
    '',
  );
  const [filterSignature, setFilterSignature] = usePersistedState(
    '@samasc:expenses:filterSignature',
    '',
  );
  const [filterStatus, setFilterStatus] = usePersistedState(
    '@samasc:expenses:filterStatus',
    '',
  );
  const [filterDateFrom, setFilterDateFrom] = usePersistedState(
    '@samasc:expenses:filterDateFrom',
    '',
  );
  const [filterDateTo, setFilterDateTo] = usePersistedState(
    '@samasc:expenses:filterDateTo',
    '',
  );
  const [filterExpenseType, setFilterExpenseType] = usePersistedState(
    '@samasc:expenses:filterExpenseType',
    '',
  );
  const [filterDueDateFrom, setFilterDueDateFrom] = usePersistedState(
    '@samasc:expenses:filterDueDateFrom',
    '',
  );
  const [filterDueDateTo, setFilterDueDateTo] = usePersistedState(
    '@samasc:expenses:filterDueDateTo',
    '',
  );

  const hasActiveFilters =
    !!filterUserId ||
    !!filterSignature ||
    !!filterStatus ||
    !!filterDateFrom ||
    !!filterDateTo ||
    !!filterExpenseType ||
    !!filterDueDateFrom ||
    !!filterDueDateTo;

  const clearFilters = () => {
    setFilterUserId('');
    setFilterSignature('');
    setFilterStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterExpenseType('');
    setFilterDueDateFrom('');
    setFilterDueDateTo('');
    setPage(1);
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return RiArrowUpDownLine;
    return sortDirection === 'asc' ? RiArrowUpLine : RiArrowDownLine;
  };

  // Calcular total (usado tanto no sort quanto na exibição).
  // O valor de cada item já vem correto na origem (ModalAddNewExpense /
  // ModalConsolidateExpenses aplicam a regra de faixa por KM antes de
  // gravar `amount`), então aqui é só somar.
  const calcTotal = (report: IExpenseReport) =>
    report.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  // IDs dos relatórios que SÃO pais consolidados, ou seja, têm pelo menos
  // um filho apontando para eles via consolidated_report_id. O backend não
  // marca o pai com nenhuma flag — ele é inferido pelo vínculo dos filhos.
  const parentReportIds = useMemo(() => {
    const ids = new Set<string>();
    expenseReports.forEach(r => {
      const parentId = r.consolidatedReportId || r.consolidated_report_id;
      if (parentId) ids.add(parentId);
    });
    return ids;
  }, [expenseReports]);

  const getSignatureRank = (report: IExpenseReport): number => {
    // Ordem: signed > sent > rejected > pending
    if (report.signature || report.signatureStatus === 'signed') return 3;
    if (report.signatureStatus === 'sent') return 2;
    if (report.signatureStatus === 'rejected') return 1;
    return 0;
  };

  // Filtrar + ordenar
  const filteredReports = useMemo(() => {
    let base = showConsolidated
      ? expenseReports
      : expenseReports.filter(report => !report.isConsolidated);

    // Aplicar filtros
    if (filterUserId) {
      base = base.filter(
        r => r.director === filterUserId || (r as any).user_id === filterUserId,
      );
    }

    if (filterSignature) {
      base = base.filter(r => {
        const sigStatus = r.signatureStatus || r.signature_status;
        if (filterSignature === 'signed') return sigStatus === 'signed' || !!r.signature;
        if (filterSignature === 'sent') return sigStatus === 'sent';
        if (filterSignature === 'pending') return !sigStatus || sigStatus === 'pending';
        if (filterSignature === 'rejected') return sigStatus === 'rejected';
        return true;
      });
    }

    if (filterStatus) {
      base = base.filter(r => {
        const status = r.status || 'EM PROCESSO';
        return status === filterStatus;
      });
    }

    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      base = base.filter(r => new Date(r.created_at) >= from);
    }

    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59, 999);
      base = base.filter(r => new Date(r.created_at) <= to);
    }

    if (filterExpenseType) {
      base = base.filter(r => r.items.some(item => item.expense_type === filterExpenseType));
    }

    if (filterDueDateFrom) {
      const from = new Date(filterDueDateFrom);
      base = base.filter(r => r.due_date && new Date(r.due_date) >= from);
    }

    if (filterDueDateTo) {
      const to = new Date(filterDueDateTo);
      to.setHours(23, 59, 59, 999);
      base = base.filter(r => r.due_date && new Date(r.due_date) <= to);
    }

    const sorted = [...base].sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case 'date':
          cmp =
            new Date(a.created_at).getTime() -
            new Date(b.created_at).getTime();
          break;
        case 'user': {
          const nameA = (
            a.director_name ||
            getUserName(a.director) ||
            ''
          ).toLowerCase();
          const nameB = (
            b.director_name ||
            getUserName(b.director) ||
            ''
          ).toLowerCase();
          cmp = nameA.localeCompare(nameB);
          break;
        }
        case 'items':
          cmp = a.items.length - b.items.length;
          break;
        case 'total':
          cmp = calcTotal(a) - calcTotal(b);
          break;
        case 'signature':
          cmp = getSignatureRank(a) - getSignatureRank(b);
          break;
        case 'status': {
          const statusOrder: Record<string, number> = {
            'EM PROCESSO': 0,
            'AGUARDANDO LANCAMENTO': 1,
            LANCADO: 2,
            RECUSADO: 3,
          };
          const sa = statusOrder[a.status || 'EM PROCESSO'] ?? 0;
          const sb = statusOrder[b.status || 'EM PROCESSO'] ?? 0;
          cmp = sa - sb;
          break;
        }
        case 'due_date': {
          const da = a.due_date ? new Date(a.due_date).getTime() : 0;
          const db = b.due_date ? new Date(b.due_date).getTime() : 0;
          cmp = da - db;
          break;
        }
        default:
          cmp = 0;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [expenseReports, showConsolidated, sortColumn, sortDirection, getUserName, filterUserId, filterSignature, filterStatus, filterDateFrom, filterDateTo, filterExpenseType, filterDueDateFrom, filterDueDateTo]);

  const paginatedReports = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filteredReports.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredReports, page]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const groupLabelPrefix: Record<'sector' | 'section' | 'cost_center', string> =
    {
      sector: 'Setor',
      section: 'Seção',
      cost_center: 'Centro de Custo',
    };

  const groupedReports = useMemo(() => {
    const map = new Map<string, IExpenseReport[]>();
    filteredReports.forEach(report => {
      const key = String((report as any)[groupBy] || 'Não informado');
      const list = map.get(key);
      if (list) {
        list.push(report);
      } else {
        map.set(key, [report]);
      }
    });

    return Array.from(map.entries())
      .map(([key, reports]) => ({
        key,
        reports,
        count: reports.length,
        total: reports.reduce((sum, r) => sum + calcTotal(r), 0),
        // Contagem por status contábil para o resumo do cabeçalho do grupo.
        posted: reports.filter(r => r.status === 'LANCADO').length,
        awaiting: reports.filter(r => r.status === 'AGUARDANDO LANCAMENTO')
          .length,
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredReports, groupBy]);

  const getItemsBreakdown = (report: IExpenseReport) => {
    const breakdown = {
      refeicao: 0,
      km: 0,
      pedagio: 0,
      outros: 0,
      ajudaCusto: 0,
      attachments: 0,
    };

    report.items.forEach(item => {
      if (item.expense_type === 'Refeição') breakdown.refeicao += 1;
      if (item.expense_type === 'Quilometragem adicional') breakdown.km += 1;
      if (item.expense_type === 'Pedágio adicional') breakdown.pedagio += 1;
      if (item.expense_type === 'Ajuda de custo') breakdown.ajudaCusto += 1;
      if (item.expense_type === 'Outros') breakdown.outros += 1;
      if (item.receipt) breakdown.attachments += 1;
    });

    return breakdown;
  };

  const handleUpdateSignatureStatus = () => {
    // O backend atualiza o status ao enviar o email.
    // Recarregar a lista para refletir o novo status.
    if (onRefresh) {
      onRefresh();
    }
  };

  const getAccountingStatusColor = (report: IExpenseReport) => {
    if (report.status === 'LANCADO') return 'green';
    if (report.status === 'RECUSADO') return 'red';
    if (report.status === 'AGUARDANDO LANCAMENTO') return 'yellow';
    return 'gray';
  };

  const getUserSignatureLabel = (report: IExpenseReport) => {
    const status = report.signatureStatus || report.signature_status;
    if (report.signature || status === 'signed') {
      const name = report.signature?.name || '';
      return { color: 'green', label: 'Assinado', tooltip: name };
    }
    if (status === 'sent') return { color: 'orange', label: 'Aguardando', tooltip: '' };
    if (status === 'rejected') return { color: 'red', label: 'Rejeitado', tooltip: '' };
    return { color: 'gray', label: 'Pendente', tooltip: '' };
  };

  const getManagerSignatureLabel = (report: IExpenseReport) => {
    const status = report.managerSignatureStatus || report.manager_signature_status;
    if (!status) return null; // Gestor não configurado
    if (report.managerSignature || status === 'signed') {
      const name = report.managerSignature?.name || '';
      return { color: 'green', label: 'Aprovado', tooltip: name };
    }
    if (status === 'sent') return { color: 'orange', label: 'Aguardando', tooltip: '' };
    if (status === 'rejected') return { color: 'red', label: 'Rejeitado', tooltip: '' };
    return { color: 'gray', label: 'Pendente', tooltip: '' };
  };

  const getSignatureStatus = (report: IExpenseReport) => {
    const userSig = getUserSignatureLabel(report);
    const mgrSig = getManagerSignatureLabel(report);

    return (
      <VStack align="start" spacing={1}>
        <Tooltip
          label={userSig.tooltip ? `Usuário: ${userSig.tooltip}` : ''}
          hasArrow
          isDisabled={!userSig.tooltip}
        >
          <HStack spacing={1} cursor={userSig.tooltip ? 'help' : 'default'}>
            {userSig.color === 'green' ? (
              <Icon as={RiShieldCheckLine} color="green.400" fontSize="14" />
            ) : null}
            <ColorBadge color={userSig.color} fontSize="2xs">
              Usu. {userSig.label}
            </ColorBadge>
          </HStack>
        </Tooltip>

        {mgrSig && (
          <Tooltip
            label={mgrSig.tooltip ? `Gestor: ${mgrSig.tooltip}` : ''}
            hasArrow
            isDisabled={!mgrSig.tooltip}
          >
            <HStack
              spacing={1}
              cursor={mgrSig.tooltip ? 'help' : 'default'}
            >
              {mgrSig.color === 'green' ? (
                <Icon
                  as={RiShieldCheckLine}
                  color="green.400"
                  fontSize="14"
                />
              ) : null}
              <ColorBadge color={mgrSig.color} fontSize="2xs">
                Gest. {mgrSig.label}
              </ColorBadge>
            </HStack>
          </Tooltip>
        )}
      </VStack>
    );
  };

  return (
    <Box flex="1" borderRadius={8} bg="gray.800" p={['4', '8']}>
      <Flex mb="6" justify="space-between" align="center">
        <Flex>
          <Heading size="md" fontWeight="normal">
            Despesas e Reembolsos
          </Heading>
          <Text color="gray.400" fontSize="xs" ml="2" mt="1">
            (total de relatórios: {filteredReports.length})
          </Text>
        </Flex>

        <Flex gap={3} align="center">
          <Button
            size="sm"
            variant={hasActiveFilters ? 'solid' : 'outline'}
            colorScheme={hasActiveFilters ? 'blue' : 'gray'}
            leftIcon={<Icon as={RiFilterLine} />}
            onClick={toggleFilters}
          >
            Filtros
            {hasActiveFilters && (
              <Badge ml={1} colorScheme="white" fontSize="2xs" borderRadius="full">
                !
              </Badge>
            )}
          </Button>

          <ModalConsolidateExpenses
            expenseReports={expenseReports}
            onConsolidate={onConsolidate}
          />
          <ModalAddNewExpense handleAddExpense={onAdd} />
        </Flex>
      </Flex>

      {/* Painel de Filtros */}
      <Collapse in={filtersOpen} animateOpacity>
        <Box
          mb="4"
          p="4"
          bg="whiteAlpha.100"
          borderRadius="md"
          border="1px solid"
          borderColor="whiteAlpha.200"
        >
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="sm" fontWeight="bold" color="gray.300">
              Filtros
            </Text>
            {hasActiveFilters && (
              <Button
                size="xs"
                variant="ghost"
                colorScheme="red"
                leftIcon={<Icon as={RiCloseLine} />}
                onClick={clearFilters}
              >
                Limpar filtros
              </Button>
            )}
          </Flex>

          <Flex gap={3} flexWrap="wrap">
            {/* Filtro por Usuário (apenas admin) */}
            {isAdmin && (
              <Box minW="200px" flex={1}>
                <Text fontSize="xs" color="gray.500" mb={1}>
                  Usuário
                </Text>
                <Select
                  size="sm"
                  value={filterUserId}
                  onChange={e => {
                    setFilterUserId(e.target.value);
                    setPage(1);
                  }}
                  bg="whiteAlpha.100"
                  borderColor="whiteAlpha.300"
                  color="gray.200"
                >
                  <option value="" style={{ background: '#fff', color: '#1a202c' }}>
                    Todos
                  </option>
                  {users.map(u => (
                    <option
                      key={u.id}
                      value={u.id}
                      style={{ background: '#fff', color: '#1a202c' }}
                    >
                      {u.name}
                    </option>
                  ))}
                </Select>
              </Box>
            )}

            {/* Filtro por Assinatura */}
            <Box minW="160px" flex={1}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Assinatura
              </Text>
              <Select
                size="sm"
                value={filterSignature}
                onChange={e => {
                  setFilterSignature(e.target.value);
                  setPage(1);
                }}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                color="gray.200"
              >
                <option value="" style={{ background: '#fff', color: '#1a202c' }}>
                  Todas
                </option>
                <option value="pending" style={{ background: '#fff', color: '#1a202c' }}>
                  Pendente
                </option>
                <option value="sent" style={{ background: '#fff', color: '#1a202c' }}>
                  Aguardando
                </option>
                <option value="signed" style={{ background: '#fff', color: '#1a202c' }}>
                  Assinado
                </option>
                <option value="rejected" style={{ background: '#fff', color: '#1a202c' }}>
                  Rejeitado
                </option>
              </Select>
            </Box>

            {/* Filtro por Status */}
            <Box minW="180px" flex={1}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Status
              </Text>
              <Select
                size="sm"
                value={filterStatus}
                onChange={e => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                color="gray.200"
              >
                <option value="" style={{ background: '#fff', color: '#1a202c' }}>
                  Todos
                </option>
                <option value="EM PROCESSO" style={{ background: '#fff', color: '#1a202c' }}>
                  Em Processo
                </option>
                <option
                  value="AGUARDANDO LANCAMENTO"
                  style={{ background: '#fff', color: '#1a202c' }}
                >
                  Aguardando Lançamento
                </option>
                <option value="LANCADO" style={{ background: '#fff', color: '#1a202c' }}>
                  Lançado
                </option>
                <option value="RECUSADO" style={{ background: '#fff', color: '#1a202c' }}>
                  Recusado
                </option>
              </Select>
            </Box>

            {/* Filtro por Tipo de Despesa */}
            <Box minW="180px" flex={1}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Tipo de Despesa
              </Text>
              <Select
                size="sm"
                value={filterExpenseType}
                onChange={e => {
                  setFilterExpenseType(e.target.value);
                  setPage(1);
                }}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                color="gray.200"
              >
                <option value="" style={{ background: '#fff', color: '#1a202c' }}>
                  Todos
                </option>
                <option value="Refeição" style={{ background: '#fff', color: '#1a202c' }}>
                  Refeição
                </option>
                <option value="Hospedagem" style={{ background: '#fff', color: '#1a202c' }}>
                  Hospedagem
                </option>
                <option value="Quilometragem adicional" style={{ background: '#fff', color: '#1a202c' }}>
                  Quilometragem adicional
                </option>
                <option value="Pedágio adicional" style={{ background: '#fff', color: '#1a202c' }}>
                  Pedágio adicional
                </option>
                <option value="Ajuda de custo" style={{ background: '#fff', color: '#1a202c' }}>
                  Ajuda de custo
                </option>
                <option value="Outros" style={{ background: '#fff', color: '#1a202c' }}>
                  Outros
                </option>
              </Select>
            </Box>

            {/* Filtro por Data */}
            <Box minW="140px" flex={1}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Data de
              </Text>
              <Input
                size="sm"
                type="date"
                value={filterDateFrom}
                onChange={e => {
                  setFilterDateFrom(e.target.value);
                  setPage(1);
                }}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                color="gray.200"
              />
            </Box>
            <Box minW="140px" flex={1}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Data até
              </Text>
              <Input
                size="sm"
                type="date"
                value={filterDateTo}
                onChange={e => {
                  setFilterDateTo(e.target.value);
                  setPage(1);
                }}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                color="gray.200"
              />
            </Box>

            {/* Mostrar consolidados */}
            <Box minW="140px" display="flex" alignItems="flex-end" pb={1}>
              <FormControl display="flex" alignItems="center" width="auto">
                <FormLabel
                  htmlFor="show-consolidated-filter"
                  mb="0"
                  fontSize="xs"
                  color="gray.400"
                >
                  Consolidados
                </FormLabel>
                <Switch
                  id="show-consolidated-filter"
                  size="sm"
                  isChecked={showConsolidated}
                  onChange={e => setShowConsolidated(e.target.checked)}
                  colorScheme="green"
                />
              </FormControl>
            </Box>
          </Flex>

          {/* Filtro por Vencimento */}
          <Flex gap={3} mt={3}>
            <Box minW="140px" flex={1}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Vencimento de
              </Text>
              <Input
                size="sm"
                type="date"
                value={filterDueDateFrom}
                onChange={e => {
                  setFilterDueDateFrom(e.target.value);
                  setPage(1);
                }}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                color="gray.200"
              />
            </Box>
            <Box minW="140px" flex={1}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Vencimento até
              </Text>
              <Input
                size="sm"
                type="date"
                value={filterDueDateTo}
                onChange={e => {
                  setFilterDueDateTo(e.target.value);
                  setPage(1);
                }}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                color="gray.200"
              />
            </Box>
          </Flex>
        </Box>
      </Collapse>

      {expenseReports.length === 0 ? (
        <Center py={10}>
          <Flex flexDirection="column" align="center">
            <Icon as={RiDraftLine} fontSize="40" color="gray.500" mb={2} />
            <Text color="gray.400">Nenhum relatório registrado</Text>
            <Text color="gray.500" fontSize="sm" mt={1}>
              Clique em &quot;Novo Relatório&quot; para começar
            </Text>
          </Flex>
        </Center>
      ) : (
        <Tabs variant="soft-rounded" colorScheme="blue">
          <TabList mb="4">
            <Tab>Lista</Tab>
            <Tab>Agrupado</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <Table
                colorScheme="whiteAlpha"
                size="md"
                sx={{ 'td, th': { borderColor: 'whiteAlpha.100' } }}
              >
                <Thead>
                  <Tr>
                    <Th
                      cursor="pointer"
                      onClick={() => handleSort('date')}
                      _hover={{ color: 'blue.300' }}
                      userSelect="none"
                    >
                      <HStack spacing={1}>
                        <Text>Data</Text>
                        <Icon
                          as={getSortIcon('date')}
                          fontSize="12"
                          color={sortColumn === 'date' ? 'blue.300' : 'gray.500'}
                        />
                      </HStack>
                    </Th>
                    <Th
                      cursor="pointer"
                      onClick={() => handleSort('user')}
                      _hover={{ color: 'blue.300' }}
                      userSelect="none"
                    >
                      <HStack spacing={1}>
                        <Text>Usuário</Text>
                        <Icon
                          as={getSortIcon('user')}
                          fontSize="12"
                          color={sortColumn === 'user' ? 'blue.300' : 'gray.500'}
                        />
                      </HStack>
                    </Th>
                    <Th
                      cursor="pointer"
                      onClick={() => handleSort('items')}
                      _hover={{ color: 'blue.300' }}
                      userSelect="none"
                    >
                      <HStack spacing={1}>
                        <Text>Itens</Text>
                        <Icon
                          as={getSortIcon('items')}
                          fontSize="12"
                          color={sortColumn === 'items' ? 'blue.300' : 'gray.500'}
                        />
                      </HStack>
                    </Th>
                    <Th
                      cursor="pointer"
                      onClick={() => handleSort('total')}
                      _hover={{ color: 'blue.300' }}
                      userSelect="none"
                    >
                      <HStack spacing={1}>
                        <Text>Valor Total</Text>
                        <Icon
                          as={getSortIcon('total')}
                          fontSize="12"
                          color={sortColumn === 'total' ? 'blue.300' : 'gray.500'}
                        />
                      </HStack>
                    </Th>
                    <Th
                      cursor="pointer"
                      onClick={() => handleSort('signature')}
                      _hover={{ color: 'blue.300' }}
                      userSelect="none"
                    >
                      <HStack spacing={1}>
                        <Text>Assinatura</Text>
                        <Icon
                          as={getSortIcon('signature')}
                          fontSize="12"
                          color={
                        sortColumn === 'signature' ? 'blue.300' : 'gray.500'
                      }
                        />
                      </HStack>
                    </Th>
                    <Th
                      cursor="pointer"
                      onClick={() => handleSort('status')}
                      _hover={{ color: 'blue.300' }}
                      userSelect="none"
                    >
                      <HStack spacing={1}>
                        <Text>Status</Text>
                        <Icon
                          as={getSortIcon('status')}
                          fontSize="12"
                          color={
                        sortColumn === 'status' ? 'blue.300' : 'gray.500'
                      }
                        />
                      </HStack>
                    </Th>
                    <Th
                      cursor="pointer"
                      onClick={() => handleSort('due_date')}
                      _hover={{ color: 'blue.300' }}
                      userSelect="none"
                    >
                      <HStack spacing={1}>
                        <Text>Vencimento</Text>
                        <Icon
                          as={getSortIcon('due_date')}
                          fontSize="12"
                          color={
                        sortColumn === 'due_date' ? 'blue.300' : 'gray.500'
                      }
                        />
                      </HStack>
                    </Th>
                    <Th width="8">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedReports.map(report => {
                const breakdown = getItemsBreakdown(report);
                const total = calcTotal(report);

                return (
                  <Tr
                    key={report.id}
                    bg={report.isConsolidated ? 'whiteAlpha.50' : undefined}
                    opacity={report.isConsolidated ? 0.6 : 1}
                    _hover={{
                      bg: report.isConsolidated
                        ? 'whiteAlpha.100'
                        : 'whiteAlpha.50',
                    }}
                  >
                    <Td py={4}>
                      <HStack spacing={2}>
                        {report.isConsolidated && (
                          <Tooltip
                            label="Relatório consolidado"
                            hasArrow
                            placement="top"
                          >
                            <Box>
                              <Icon
                                as={RiCheckboxCircleLine}
                                color="green.300"
                                fontSize="18"
                              />
                            </Box>
                          </Tooltip>
                        )}
                        <Text>{formatReportDate(report.date)}</Text>
                      </HStack>
                    </Td>
                    <Td py={4}>
                      <HStack spacing={2}>
                        <Text>
                          {report.director_name || getUserName(report.director)}
                        </Text>
                        {report.isConsolidated && (
                          <ColorBadge color="green" fontSize="xs">
                            Consolidado
                          </ColorBadge>
                        )}
                        {parentReportIds.has(report.id) && (
                          <Tooltip
                            hasArrow
                            label="Relatório consolidado (consolida outros)"
                            placement="top"
                          >
                            <Box as="span" display="inline-flex">
                              <Icon
                                as={RiGitMergeLine}
                                color="purple.300"
                                fontSize="16"
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                    <Td py={4}>
                      <HStack spacing={1} flexWrap="wrap">
                        <ColorBadge color="gray">
                          {report.items.length}{' '}
                          {report.items.length === 1 ? 'item' : 'itens'}
                        </ColorBadge>
                        {breakdown.refeicao > 0 && (
                          <ColorBadge color="green" fontSize="xs">
                            {breakdown.refeicao} ref
                          </ColorBadge>
                        )}
                        {breakdown.km > 0 && (
                          <ColorBadge color="blue" fontSize="xs">
                            {breakdown.km} km
                          </ColorBadge>
                        )}
                        {breakdown.pedagio > 0 && (
                          <ColorBadge color="orange" fontSize="xs">
                            {breakdown.pedagio} ped
                          </ColorBadge>
                        )}
                        {breakdown.ajudaCusto > 0 && (
                          <ColorBadge color="purple" fontSize="xs">
                            {breakdown.ajudaCusto} aj. custo
                          </ColorBadge>
                        )}
                        {breakdown.outros > 0 && (
                          <ColorBadge color="teal" fontSize="xs">
                            {breakdown.outros} out
                          </ColorBadge>
                        )}
                        {breakdown.attachments > 0 && (
                          <Tooltip
                            label={`${breakdown.attachments} ${
                              breakdown.attachments === 1
                                ? 'comprovante anexado'
                                : 'comprovantes anexados'
                            }`}
                            hasArrow
                            placement="top"
                          >
                            <HStack
                              spacing={1}
                              bg="whiteAlpha.100"
                              px={2}
                              py={0.5}
                              borderRadius="md"
                              cursor="help"
                            >
                              <Icon
                                as={RiAttachmentLine}
                                fontSize="12"
                                color="teal.300"
                              />
                              <Text
                                fontSize="xs"
                                color="teal.300"
                                fontWeight="bold"
                              >
                                {breakdown.attachments}
                              </Text>
                            </HStack>
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                    <Td py={4}>
                      <Text fontWeight="bold">{formatCurrency(total)}</Text>
                    </Td>
                    <Td py={4}>{getSignatureStatus(report)}</Td>
                    <Td py={4}>
                      <ColorBadge
                        color={getAccountingStatusColor(report)}
                        fontSize="2xs"
                      >
                        {report.status || 'EM PROCESSO'}
                      </ColorBadge>
                    </Td>
                    <Td py={4}>
                      <Text fontSize="sm" color={report.due_date ? 'gray.300' : 'gray.600'}>
                        {formatReportDate(report.due_date, 'dd/MM/yyyy')}
                      </Text>
                    </Td>
                    <Td py={4}>
                      <HStack spacing={1}>
                        <ModalViewExpense expense={report} />

                        {/* Drill-through: ver os relatórios consolidados (filhos).
                            Aparece apenas no relatório PAI (que tem filhos). */}
                        {parentReportIds.has(report.id) && (
                          <ModalConsolidatedChildren
                            consolidated={report}
                            allReports={expenseReports}
                          />
                        )}

                        {/* Reverter ressarcimento (modo admin - permission
                            edit_ressarcimento). Só aparece quando o relatório
                            está travado por assinatura/envio ou já lançado, que
                            é justamente quando a edição normal fica bloqueada.
                            O gate de permission é feito dentro do próprio modal. */}
                        {!report.isConsolidated &&
                          (report.signature ||
                            report.signatureStatus === 'signed' ||
                            report.signatureStatus === 'sent' ||
                            report.managerSignatureStatus === 'signed' ||
                            report.managerSignatureStatus === 'sent' ||
                            report.status === 'AGUARDANDO LANCAMENTO' ||
                            report.status === 'LANCADO' ||
                            report.status === 'RECUSADO') && (
                            <ModalRevertRessarcimento
                              report={report}
                              onReverted={() => {
                                if (onRefresh) onRefresh();
                              }}
                            />
                          )}

                        {/* Enviar para assinatura do usuário */}
                        {!report.isConsolidated &&
                          !report.signature &&
                          report.signatureStatus !== 'sent' &&
                          report.signatureStatus !== 'signed' && (
                            <ModalSendForSignature
                              report={report}
                              onStatusUpdate={handleUpdateSignatureStatus}
                            />
                          )}

                        {/* Enviar para aprovação do gestor (após usuário assinar) */}
                        {report.manager_id &&
                          (report.signature ||
                            report.signatureStatus === 'signed') &&
                          report.managerSignatureStatus !== 'signed' &&
                          report.manager_signature_status !== 'signed' &&
                          report.managerSignatureStatus !== 'sent' &&
                          report.manager_signature_status !== 'sent' && (
                            <ModalSendForSignature
                              report={report}
                              onStatusUpdate={handleUpdateSignatureStatus}
                              isManager
                            />
                          )}

                        {!report.signature &&
                          report.signatureStatus !== 'signed' && (
                            <ModalEditExpense
                              expense={report}
                              handleEditExpense={onEdit}
                            />
                          )}

                        {!report.signature &&
                          report.signatureStatus !== 'signed' && (
                            <Tooltip hasArrow label="Excluir">
                              <Button
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => onDelete(report.id)}
                              >
                                <Icon as={RiDeleteBinLine} fontSize="20" />
                              </Button>
                            </Tooltip>
                          )}

                        {/* Enviar para Lançamento Contábil - ambas assinaturas feitas
                            (ou sem gestor vinculado, caso de membros da diretoria)
                            e não enviado ainda */}
                        {(report.signature ||
                          report.signatureStatus === 'signed') &&
                          (!report.manager_id ||
                            report.managerSignature ||
                            report.managerSignatureStatus === 'signed' ||
                            report.manager_signature_status === 'signed') &&
                          report.status !== 'AGUARDANDO LANCAMENTO' &&
                          report.status !== 'LANCADO' &&
                          report.status !== 'RECUSADO' && (
                            <Tooltip
                              hasArrow
                              label="Enviar para Lançamento Contábil"
                            >
                              <Button
                                size="sm"
                                colorScheme="yellow"
                                variant="ghost"
                                isLoading={sendingAccountingId === report.id}
                                onClick={async () => {
                                  setSendingAccountingId(report.id);
                                  try {
                                    await expenseReportsService.sendToAccounting(
                                      report.id,
                                    );
                                    apllyToast(
                                      'success',
                                      'Relatório enviado para lançamento contábil!',
                                    );
                                    if (onRefresh) onRefresh();
                                  } catch (err: any) {
                                    apllyToast(
                                      'error',
                                      err.response?.data?.message ||
                                        'Erro ao enviar para contábil',
                                    );
                                  } finally {
                                    setSendingAccountingId('');
                                  }
                                }}
                              >
                                <Icon as={RiSendPlaneLine} fontSize="20" />
                              </Button>
                            </Tooltip>
                          )}

                        {/* Alterar status - apenas admin (flag_9) */}
                        {isAdmin &&
                          report.status === 'AGUARDANDO LANCAMENTO' && (
                            <ModalUpdateExpenseStatus
                              report={report}
                              onStatusUpdated={() => {
                                if (onRefresh) onRefresh();
                              }}
                            />
                          )}
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
                </Tbody>
              </Table>

              <Pagination
                totalCountOfRegisters={filteredReports.length}
                currentPage={page}
                onPageChange={setPage}
                registersPerPage={itemsPerPage}
              />
            </TabPanel>

            <TabPanel px={0}>
              <Flex mb="4" align="center" gap={3}>
                <Text fontSize="sm" color="gray.400">
                  Agrupar por
                </Text>
                <Select
                  size="sm"
                  maxW="240px"
                  value={groupBy}
                  onChange={e =>
                    setGroupBy(
                      e.target.value as 'sector' | 'section' | 'cost_center',
                    )}
                  bg="whiteAlpha.100"
                  borderColor="whiteAlpha.300"
                  color="gray.200"
                >
                  <option
                    value="sector"
                    style={{ background: '#fff', color: '#1a202c' }}
                  >
                    Setor
                  </option>
                  <option
                    value="section"
                    style={{ background: '#fff', color: '#1a202c' }}
                  >
                    Seção
                  </option>
                  <option
                    value="cost_center"
                    style={{ background: '#fff', color: '#1a202c' }}
                  >
                    Centro de Custo
                  </option>
                </Select>
              </Flex>

              <Accordion allowMultiple>
                {groupedReports.map(group => (
                  <AccordionItem key={group.key} border="none" mb={2}>
                    <AccordionButton
                      py={3}
                      borderRadius="md"
                      _hover={{ bg: 'whiteAlpha.100' }}
                    >
                      <Flex flex="1" align="center" justify="space-between">
                        <Text fontWeight="bold">
                          {`${groupLabelPrefix[groupBy]} ${group.key}`}
                        </Text>
                        <HStack spacing={3}>
                          {group.posted > 0 && (
                            <Tooltip
                              hasArrow
                              label="Relatórios já lançados"
                              placement="top"
                            >
                              <Box>
                                <ColorBadge color="green" fontSize="2xs">
                                  {group.posted} lançado
                                  {group.posted > 1 ? 's' : ''}
                                </ColorBadge>
                              </Box>
                            </Tooltip>
                          )}
                          {group.awaiting > 0 && (
                            <Tooltip
                              hasArrow
                              label="Relatórios aguardando lançamento"
                              placement="top"
                            >
                              <Box>
                                <ColorBadge color="yellow" fontSize="2xs">
                                  {group.awaiting} aguardando
                                </ColorBadge>
                              </Box>
                            </Tooltip>
                          )}
                          <Badge colorScheme="blue">
                            {group.count} relatórios
                          </Badge>
                          <Text fontWeight="bold" color="green.300">
                            {formatCurrency(group.total)}
                          </Text>
                        </HStack>
                      </Flex>
                      <AccordionIcon ml={2} />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <Table
                        size="sm"
                        colorScheme="whiteAlpha"
                        sx={{ 'td, th': { borderColor: 'whiteAlpha.100' } }}
                      >
                        <Thead>
                          <Tr>
                            <Th>Data</Th>
                            <Th>Usuário</Th>
                            <Th>Itens</Th>
                            <Th>Valor</Th>
                            <Th>Status</Th>
                            <Th width="8">Ações</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {group.reports.map(report => (
                            <Tr key={report.id}>
                              <Td py={4}>
                                {formatReportDate(report.date)}
                              </Td>
                              <Td py={4}>
                                {report.director_name ||
                                  getUserName(report.director)}
                              </Td>
                              <Td py={4}>
                                <Badge colorScheme="gray">
                                  {report.items.length}{' '}
                                  {report.items.length === 1
                                    ? 'item'
                                    : 'itens'}
                                </Badge>
                              </Td>
                              <Td py={4}>
                                <Text fontWeight="bold">
                                  {formatCurrency(calcTotal(report))}
                                </Text>
                              </Td>
                              <Td py={4}>
                                <ColorBadge
                                  color={getAccountingStatusColor(report)}
                                  fontSize="2xs"
                                >
                                  {report.status || 'EM PROCESSO'}
                                </ColorBadge>
                              </Td>
                              <Td py={4}>
                                <ModalViewExpense expense={report} />
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
}
